/* eslint-disable import/order */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiBookOpen } from 'react-icons/fi';
import { IoIosArrowForward } from 'react-icons/io';
import { AiOutlineClockCircle } from 'react-icons/ai';

import { useEvieAppContext } from '../EvieAppProvider';
import { useEvieHomeContext } from '../EvieHomeProvider';
import showNotification from 'utils/showNotification';
import { ROUTES } from 'utils/constants';
import { HOME_STATUS, ACTIVITY_TYPES, getSqueezeDuration } from '../utils';
import womanBall from 'assets/images/woman-ball.webp';
import vulva from 'assets/images/mid-vulva.webp';

import LockedIcon from '../LockedIcon';

import './daily-tasks-card.scss';

export const getUnlockTime = (completedAt) => {
  const completedDate = new Date(completedAt);

  const nextDay = new Date(completedDate);
  nextDay.setDate(completedDate.getDate() + 1);
  nextDay.setHours(0, 0, 0, 0);
  return nextDay;
};

export const isContentUnlocked = (completedAt) => {
  const unlockTime = getUnlockTime(completedAt);
  return new Date() >= unlockTime;
};

const DailyTasksCard = () => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const {
    additionalContent,
    completedActivities,
    personalInfo: { currentDay, totalDays, dayContent, completedDate },
  } = useEvieAppContext();
  const { changeHomeStatus, setActivityType, selectTask } = useEvieHomeContext();
  const taskIds = completedActivities.map(({ id }) => id);
  const reading = additionalContent.find(({ __typename, id }) => __typename === 'Reading' && !taskIds.includes(id));
  const isWeekCompleted = currentDay === totalDays;
  const lockTime = getUnlockTime(completedDate).toLocaleString();
  const isUnlocked = isContentUnlocked(completedDate);

  const handleGoToProgram = () => {
    if (isWeekCompleted) {
      push(`${ROUTES.EVIE_ONBOARDING}?view=plans`);
    } else {
      push(ROUTES.EVIE_MY_PROGRAM);
    }
  };

  const handleGoToTask = ({ isExercise, isWeekCompleted: completed, currentTask }) => {
    if (completed) return;
    if (!isUnlocked) {
      showNotification({ messages: [`Content locked until ${lockTime}`] });
      return;
    }
    selectTask(currentTask);
    if (isExercise) {
      setActivityType(ACTIVITY_TYPES.EXERCISE);
      changeHomeStatus(HOME_STATUS.SESSION);
    } else {
      setActivityType(ACTIVITY_TYPES.SQUEEZE);
      changeHomeStatus(HOME_STATUS.SQUEEZE);
    }
  };

  const handleGoToReading = () => {
    if (isWeekCompleted) return;
    if (!isUnlocked) {
      showNotification({ messages: [`Content locked until ${lockTime}`] });
      return;
    }
    changeHomeStatus(HOME_STATUS.TASK);
    selectTask(reading);
  };

  return (
    <section className="daily-tasks-card">
      <h2 className="daily-tasks-card_title">
        {t('evieApp.dailyTasksCard.day')} {currentDay} · {t('evieApp.dailyTasksCard.todaysPlan')}
      </h2>
      <p className="daily-tasks-card_status">
        {currentDay - 1} / {totalDays} {t('evieApp.dailyTasksCard.completed')}
      </p>
      <div className="daily-tasks-card__progress">
        <div
          className="daily-tasks-card__progress_current"
          style={{ width: `${((currentDay - 1) * 100) / totalDays}%` }}
        />
      </div>
      <ul className="daily-tasks-card__tasks">
        {dayContent.map((currentTask) => {
          const { __typename, id, title, exercises, squeezes, completedSessionStatus, laps = 1 } = currentTask;
          const isNotCompleted = !completedSessionStatus;
          const isExercise = __typename === ACTIVITY_TYPES.EXERCISE;
          const exDuration =
            ((exercises || []).reduce((acc, { exercise: { duration } }) => acc + Number(duration), 0) / 60) * laps;
          const sqDuration = getSqueezeDuration(squeezes) / 60;
          const taskDuration = Math.ceil(isExercise ? exDuration : sqDuration);

          return (
            <li
              key={id}
              aria-hidden="true"
              className="daily-tasks-card__tasks_task"
              onClick={() => handleGoToTask({ isExercise, isWeekCompleted, currentTask })}
            >
              <div
                className={`daily-tasks-card__tasks_taskimg ${isNotCompleted ? 'not-completed-task' : ''}`}
                style={{ backgroundImage: `url(${isExercise ? womanBall : vulva})` }}
              />
              <div className="daily-tasks-card__tasks_tasktitle">
                <h3>{title}</h3>
                <span>
                  <AiOutlineClockCircle /> {taskDuration} min
                </span>
              </div>
              <div className="daily-tasks-card__tasks_taskicon">
                {isWeekCompleted ? <LockedIcon /> : <IoIosArrowForward size={24} />}
              </div>
            </li>
          );
        })}
      </ul>
      {reading ? (
        <ul className="daily-tasks-card__tasks">
          <li aria-hidden="true" className="daily-tasks-card__tasks_task" onClick={handleGoToReading}>
            <div className="daily-tasks-card__tasks_taskimg not-completed-task">
              <FiBookOpen size={28} color="#9b2c51" />
            </div>
            <div className="daily-tasks-card__tasks_tasktitle">
              <h3>{reading.title}</h3>
              <span>
                <AiOutlineClockCircle /> {reading.duration || 3} min
              </span>
            </div>
            <div className="daily-tasks-card__tasks_taskicon">
              {isWeekCompleted ? <LockedIcon /> : <IoIosArrowForward size={24} />}
            </div>
          </li>
        </ul>
      ) : null}
      <button type="button" className="daily-tasks-card_btn" onClick={handleGoToProgram}>
        {isWeekCompleted ? t('evieApp.dailyTasksCard.lockedBtn') : t('evieApp.dailyTasksCard.unlockedBtn')}
      </button>
    </section>
  );
};

export default DailyTasksCard;
